/**
 * PortfolioDetailPage Component
 * Shows portfolio details with properties list
 */

import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getPortfolio,
  updatePortfolio,
  deletePortfolio,
  listProperties,
  type PortfolioWithStats,
  type PropertyWithStats,
} from '../lib/api';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { PageHeader } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../utils/date';

export function PortfolioDetailPage() {
  const { portfolioId } = useParams<{ portfolioId: string }>();
  const navigate = useNavigate();

  const [portfolio, setPortfolio] = useState<PortfolioWithStats | null>(null);
  const [properties, setProperties] = useState<PropertyWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editName, setEditName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!portfolioId) return;

    try {
      setIsLoading(true);
      setError(null);

      const [portfolioRes, propertiesRes] = await Promise.all([
        getPortfolio(portfolioId),
        listProperties({ portfolioId }),
      ]);

      setPortfolio(portfolioRes.portfolio);
      setProperties(propertiesRes.properties);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolio');
    } finally {
      setIsLoading(false);
    }
  }, [portfolioId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!portfolioId || !editName.trim()) return;

    try {
      setIsUpdating(true);
      setUpdateError(null);
      const response = await updatePortfolio(portfolioId, { name: editName.trim() });
      setPortfolio((prev) => prev ? { ...prev, ...response.portfolio } : null);
      setIsEditModalOpen(false);
    } catch (err) {
      setUpdateError(err instanceof Error ? err.message : 'Failed to update portfolio');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!portfolioId) return;

    try {
      setIsDeleting(true);
      await deletePortfolio(portfolioId);
      navigate('/portfolios');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete portfolio');
      setIsDeleteDialogOpen(false);
    } finally {
      setIsDeleting(false);
    }
  };

  const openEditModal = () => {
    setEditName(portfolio?.name || '');
    setUpdateError(null);
    setIsEditModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <Skeleton className="h-4 w-32 mb-4" />
        <Skeleton className="h-8 w-56 mb-2" />
        <Skeleton className="h-4 w-40 mb-6" />
        <div className="flex gap-4 mb-8">
          <Skeleton className="h-24 w-36 rounded-lg" />
          <Skeleton className="h-24 w-36 rounded-lg" />
        </div>
        <Skeleton className="h-64 w-full rounded-lg" />
      </div>
    );
  }

  if (error && !portfolio) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="text-center py-16 px-6 rounded-lg border border-border bg-card">
          <h2 className="text-xl font-semibold text-foreground mb-3">Error Loading Portfolio</h2>
          <p className="text-sm text-destructive mb-6">{error}</p>
          <div className="flex justify-center gap-3">
            <Button onClick={fetchData}>Retry</Button>
            <Button variant="outline" onClick={() => navigate('/portfolios')}>
              Back to Portfolios
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
        <div className="text-center py-16 px-6 rounded-lg border border-border bg-card">
          <h2 className="text-xl font-semibold text-foreground mb-3">Portfolio Not Found</h2>
          <Button variant="outline" onClick={() => navigate('/portfolios')}>
            Back to Portfolios
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-6 px-[var(--page-padding-x)]">
      <PageHeader
        title={portfolio.name}
        subtitle={`Created ${formatDateTime(portfolio.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}`}
        backLink={{ to: '/portfolios', label: 'Portfolios' }}
        action={
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={openEditModal}>
              Edit
            </Button>
            <Button variant="destructive" size="sm" onClick={() => setIsDeleteDialogOpen(true)}>
              Delete
            </Button>
          </div>
        }
      />

      {/* Stat cards */}
      <div className="flex gap-4 mb-8">
        <div className="flex flex-col p-5 bg-card rounded-lg border border-border min-w-[140px]">
          <span className="text-3xl font-bold text-primary">
            {portfolio.propertyCount ?? properties.length}
          </span>
          <span className="text-xs text-muted-foreground mt-1">Properties</span>
        </div>
        <div className="flex flex-col p-5 bg-card rounded-lg border border-border min-w-[140px]">
          <span className="text-3xl font-bold text-primary">
            {portfolio.unitCount ?? 0}
          </span>
          <span className="text-xs text-muted-foreground mt-1">Total Units</span>
        </div>
      </div>

      {/* Properties list */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex justify-between items-center mb-5">
          <h2 className="text-lg font-semibold text-foreground m-0">Properties</h2>
          <Button
            size="sm"
            onClick={() => navigate('/properties')}
          >
            Add Property
          </Button>
        </div>

        {properties.length === 0 ? (
          <div className="text-center py-10 px-5">
            <p className="text-sm text-muted-foreground mb-4">No properties in this portfolio yet.</p>
            <Button onClick={() => navigate('/properties')}>
              Add Your First Property
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {properties.map((property) => (
              <div
                key={property.id}
                className="flex justify-between items-center p-4 bg-accent/50 rounded-md cursor-pointer hover:bg-accent transition-colors"
                onClick={() => navigate(`/properties/${property.id}`)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    navigate(`/properties/${property.id}`);
                  }
                }}
              >
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-foreground m-0 mb-1 truncate">
                    {property.name}
                  </h3>
                  <p className="text-xs text-muted-foreground m-0">
                    {property.propertyType.replace('_', ' ')}
                  </p>
                </div>
                <div className="flex items-center gap-4 shrink-0">
                  <span className="px-2 py-0.5 text-sm font-medium text-primary bg-primary/10 rounded capitalize">
                    {property.propertyType.replace('_', ' ')}
                  </span>
                  <span className="text-xs text-muted-foreground">{property.unitCount ?? 0} units</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        title="Edit Portfolio"
      >
        <form onSubmit={handleEditSubmit}>
          {updateError && (
            <div className="p-3 mb-4 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
              {updateError}
            </div>
          )}
          <div className="mb-5">
            <label
              htmlFor="editName"
              className="block mb-2 text-sm font-medium text-foreground"
            >
              Portfolio Name
            </label>
            <input
              id="editName"
              type="text"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              autoFocus
              required
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsEditModalOpen(false)}
              disabled={isUpdating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isUpdating || !editName.trim()}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={handleDelete}
        title="Delete Portfolio"
        message={`Are you sure you want to delete "${portfolio.name}"? This will also delete all properties and units in this portfolio. This action cannot be undone.`}
        confirmText="Delete Portfolio"
        isDestructive
        isLoading={isDeleting}
      />
    </div>
  );
}

export default PortfolioDetailPage;
